import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000IdColumnDef, type U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useEffect, useRef } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU3142Context } from "../U3142Context";
import { doDeleteData, doExportExcel, doSearch, luHandleAppiError, type U3142LotDetail } from "../U3142Service";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { NS, U3142ROLES } from "../U3142";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U3142LotDeleteDlg from "./common/U3142LotDeleteDlg";
import { U3142LotListToolBar } from "./list/U3142LotListToolBar";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { U3142LotStateEnum } from "./common/lotDetailStateTypes";
import { getOperationTypeLabel } from "../../u3141LandRegister/components/common/U3141LotOperationType";
import { buildU3142OperationTypeCriteriaOptions } from "./list/U3142LotOperationTypeCriteria";
import type { U3141LotDetailOperationTypeFromInitLU } from "../../u3141LandRegister/U3141Service";
import {
  U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_CADASTRES,
  U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_DISTRICTS,
  U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_LOTS,
  defaultItemToDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import {
  U2000_CRITERIA_INIT_OPTIONS_U3142_LOT_DETAIL_OPERATION_TYPES,
  registerCriteriaInitOptionsResolver,
  unregisterCriteriaInitOptionsResolver,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaInitOptionsRegistry";
import type { U2000CriteriaStaticOption } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import {
  useU3142FindSearchCadastresHandlers,
  useU3142FindSearchDistrictsHandlers,
  useU3142FindSearchLotsHandlers,
} from "./list/U3142UseFindSearchHandlers";
import { u3142FindSearchCadastreItemToDisplay } from "./list/U3142FindSearchCadastreDisplay";

export function useU3142LotListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

function buildLotStateCriteriaOptions(lang: string): U2000CriteriaStaticOption[] {
  return U3142LotStateEnum.map((s) => ({
    value: s.value,
    label: typeof s.label === "object" ? s.label[lang as "fr" | "en"] ?? s.value : String(s.label),
  }));
}

// Function to get columns definition
function getColumnsDef(t: TFunction, lang: string = "fr", operationTypes?: U3141LotDetailOperationTypeFromInitLU[]): U2000TableColumn[] {
  const lotStateOptions = buildLotStateCriteriaOptions(lang);

  return [
    U2000IdColumnDef,
    {
      name: "lot",
      label: t("lblLot", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_LOTS,
        searchCriterionField: "lot",
        valueKey: "lot",
        criteriaField: "lot",
        displayKeys: ["lot"],
        placeholder: t("phCriteriaLot", { ns: "common" }),
      },
    },
    {
      name: "partieDeLot",
      label: t("lblPartieDeLot", { ns: NS }),
      widthClass: "w-[100px]",
      type: "string",
      align: "center",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      isDefaultSortedColumn: false,
      display: (_column, value: boolean | undefined) => (
        <span>{value === true ? t("lblYes", { ns: NS }) : value === false ? t("lblNo", { ns: NS }) : ""}</span>
      ),
    },
    {
      name: "dateEstablishment",
      label: t("lblDateEstablishment", { ns: NS }),
      widthClass: "w-[150px]",
      type: "date",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: "lotState",
      label: t("lblLotState", { ns: NS }),
      widthClass: "w-[150px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ["equals", "notequals", "in"],
      criteriaStaticOptions: lotStateOptions,
      display: (_column, value) => {
        const state = U3142LotStateEnum.find((s) => s.value === value);
        const label = state ? (typeof state.label === "object" ? state.label[lang as "fr" | "en"] : state.label) : value;
        return <span className="font-bold">{label}</span>;
      },
    },
    {
      name: "district",
      label: t("lblDistrict", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_DISTRICTS,
        searchCriterionField: "propertyDistrict",
        valueKey: "id",
        criteriaField: "district.id",
        displayKeys: ["codeDistrict", "propertyDistrict"],
        selectedDisplayKeys: ["propertyDistrict"],
        placeholder: t("phCriteriaDistrict", { ns: "common" }),
      },
      display: (_column, value) => {
        return value?.propertyDistrict || value?.codeDistrict || "";
      },
    },
    {
      name: "cadastre",
      label: t("lblCadastre", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_CADASTRES,
        searchCriterionField: "cadastre",
        valueKey: "id",
        criteriaField: "cadastre.id",
        displayKeys: ["cadastre", "cadastreCode"],
        selectedDisplayKeys: ["cadastre"],
        placeholder: t("phCriteriaCadastre", { ns: "common" }),
      },
      display: (_column, value) => {
        if (value?.cadastre && value?.rankLabel) {
          return `${value.cadastre} ${value.rankLabel}`;
        }
        return value?.cadastre || "";
      },
    },
    {
      name: "operationType",
      label: t("lblOperationType", { ns: NS }),
      widthClass: "w-[150px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "in"],
      criteriaInitOptions: {
        optionsKey: U2000_CRITERIA_INIT_OPTIONS_U3142_LOT_DETAIL_OPERATION_TYPES,
        valueKey: "operationTypeCode",
        filterActive: true,
      },
      display: (_column, value) => {
        return <span>{getOperationTypeLabel(value ?? "", lang, operationTypes)}</span>;
      },
    },
    {
      name: "documents",
      label: t("lblDocuments", { ns: NS }),
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      sortable: false,
      display: (_column, value: U3142LotDetail["documents"]) => {
        if (!value || !Array.isArray(value)) return "";
        if (value.length === 0) return "";
        if (value.length === 1) return value[0]?.documentName ?? "";
        return `${value.length} documents`;
      },
    },
    {
      name: "createdAt",
      label: t("lblFldCreatedAt", { ns: NS }),
      widthClass: "w-[150px]",
      isDefaultColumn: true,
      type: "date",
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
      isCriteriaField: true,
    },
    {
      name: "deletedAt",
      label: t("lblCndFieldDeletedAt", { ns: NS }),
      widthClass: "w-[400px]",
      type: "date",
      inGridField: true,
      align: "center",
      display: (_column, value) => {
        return value ? <span className="font-bold text-red-600">DELETED</span> : "";
      },
      includeConditions: ["isNotNull"],
      ovverideLabelConditions: [
        {
          name: "isNotNull",
          label: t("lblCndDeletedAt", { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}

const U3142LotList: React.FC = () => {
  // Navigation
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();

  // Toast
  const toast = useToast();

  // Auth context for role checking
  const { hasRole } = useU2000AuthContext();

  // Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers, unitInitLU } = useU3142Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findSearchLotsHandlers = useU3142FindSearchLotsHandlers();
  const findSearchDistrictsHandlers = useU3142FindSearchDistrictsHandlers();
  const findSearchCadastresHandlers = useU3142FindSearchCadastresHandlers();

  useEffect(() => {
    const lang = i18n?.language || "fr";
    registerCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3142_LOT_DETAIL_OPERATION_TYPES, () =>
      buildU3142OperationTypeCriteriaOptions(lang, unitInitLU.lotDetailOperationTypes),
    );
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_LOTS, {
      search: findSearchLotsHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_DISTRICTS, {
      search: findSearchDistrictsHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_CADASTRES, {
      search: findSearchCadastresHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3142FindSearchCadastreItemToDisplay(item) || defaultItemToDisplay(item, config),
    });
    return () => {
      unregisterCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3142_LOT_DETAIL_OPERATION_TYPES);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_LOTS);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_DISTRICTS);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3142_FIND_SEARCH_CADASTRES);
    };
  }, [
    findSearchLotsHandlers.search,
    findSearchDistrictsHandlers.search,
    findSearchCadastresHandlers.search,
    i18n?.language,
    unitInitLU.lotDetailOperationTypes,
  ]);

  // Initialize columns on mount or when 't' changes
  useEffect(() => {
    const lang = i18n?.language || "fr";
    const cols = getColumnsDef(t, lang, unitInitLU.lotDetailOperationTypes);
    searchStateHandlers.setColumns(cols);
    gridStateHandlers.setColumns(cols);
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t, i18n, unitInitLU.lotDetailOperationTypes]);

  // Manage Handler Actions
  const u3142LotListHandlerActions: U2000TableHandlers<U3142LotDetail> = useU3142LotListHandlerActions<U3142LotDetail>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (lot: U3142LotDetail) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === lot.id) : -1;
      if (gridStateHandlers) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`${lot.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3142");
      navigate("new");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3142");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  });

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U3142LotDetail>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3142LotDetail>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3142";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const singleLot = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === singleLot.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, singleLot.id), { replace: true });
      }

      // Nettoyer les données du quick search après la recherche (qu'il y ait navigation ou non)
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Export Excel Handlers for Logical Unit
  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Écouter les notifications WebSocket pour les exports Excel terminés
  useU2000ExportExcelNotification();

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3142",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["lot"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Après suppression, aller au prochain enregistrement, aller vers l'avant si possible, sinon vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // s'assurer que les colonnes sont chargées avant le rendu car creer des problemes d'affichage avec les columnes par defaut si searchStateHandlers.columns est vide au rendu
  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  // Render
  return (
    <>
      <U3142LotDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U3142LotListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={u3142LotListHandlerActions as any} />}
        handlerActions={u3142LotListHandlerActions}
        label={t("lblLot", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3142ROLES.EXPORT)}
      />
    </>
  );
};

export default U3142LotList;
